import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Grid,
    TextField,
    IconButton,
    Button,
    Box,
    Typography,
    CircularProgress,
    MenuItem
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';

import { Formik } from 'formik';
import * as Yup from 'yup';

import request from '../../utils/request';
import SnackbarAlert from '../common/SnackbarAlert';
import NoDataAvailable from '../common/NoDataAvailable';
import loader from '../common/loader.gif';

const SUPPORTED_FORMATS = [
    'image/jpeg', // JPG
    'image/png',  // PNG
    'application/pdf', // PDF
    'application/msword', // DOC
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
    'application/vnd.ms-excel', // XLS
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' // XLSX
];


/* ---------------- VALIDATION ---------------- */
const rowSchema = Yup.object({
    documentName: Yup.string()
        .trim()
        .required('Document Name is required'),

    file: Yup.mixed()
        .required('File is required')
        .test(
            'fileFormat',
            'Uploaded file has unsupported format',
            (value) => {
                if (!value) return false;
                return SUPPORTED_FORMATS.includes(value.type);
            }
        )
});


const EndorsementAttachmentDialog = ({
    onClose,
    endorsementId,
    policyId
}) => {
    const [rows, setRows] = useState<any[]>([]);
    const [loadingList, setLoadingList] = useState(false);
    const [savingRowId, setSavingRowId] = useState<number | null>(null);
    const [newRowStatus, setNewRowStatus] = useState(false);
    const [documentNames, setDocumentNameList] = useState([]);
    const [showSnackBarAlert, setShowSnackBarAlert] = useState({
        status: false,
        type: 'success',
        message: null
    });

    useEffect(() => {
        fetchDocuments();
        getDocumentNameList();
    }, []);



    const getDocumentNameList = async () => {
        request({
            url: 'documentName/getDocumentNameList',
            method: 'POST',
            data: {
                search: null,
                modules: ['Endorsement'],
                offset: 0,
                limit: 50000,
                orderColumn: 'id',
                orderDirection: 'DESC'
            }
        }).then(
            (response: any) => {
                setDocumentNameList(
                    response?.documentNameData || []
                );
            },
            (error) => {
                console.log(error);
            }
        );
    };

    /* ---------------- FETCH ---------------- */
    const fetchDocuments = () => {
        setLoadingList(true);
        request({
            url: 'endorsement/getEndorsementDocuments',
            method: 'POST',
            data: { id: endorsementId }
        })
            .then((res) => {
                const docs = res?.['rows'] || [];
                setRows(
                    docs.map((d) => ({
                        id: d.id,
                        documentName: d.documentName,
                        documentUrl: d.documentUrl,
                        isNew: false
                    }))
                );
            })
            .finally(() => setLoadingList(false));
    };

    /* ---------------- ADD ---------------- */
    const addRow = () => {
        setNewRowStatus(true);
        setRows((prev) => [
            {
                tempId: Date.now(),
                documentName: '',
                file: null,
                isNew: true
            },
            ...prev
        ]);
    };

    /* ---------------- DELETE ---------------- */
    const deleteRow = (row) => {
        if (row.isNew) {
            setNewRowStatus(false)
            setRows((prev) => prev.filter((r) => r.tempId !== row.tempId));
            return;
        }

        setSavingRowId(row.id);
        request({
            url: `endorsement/endoresementDocument/${row.id}`,
            method: 'DELETE'
        }).then((res: any) => {
            setNewRowStatus(false);
            setSavingRowId(null);
            fetchDocuments();
            setShowSnackBarAlert({
                status: true,
                type: 'success',
                message: res?.message
            });
        });
    };

    /* ---------------- SAVE ---------------- */
    const saveRow = (row, values) => {
        setSavingRowId(row.tempId);

        const formData = new FormData();
        formData.append('endorsementId', endorsementId);
        formData.append('policyId', policyId);
        formData.append('documentName', values.documentName);
        formData.append('documentType', 'supporting document');
        formData.append('file', values.file);

        request({
            url: 'endorsement/upload-document',
            method: 'POST',
            data: formData
        }).then((res: any) => {
            setNewRowStatus(false)
            setSavingRowId(null);
            fetchDocuments();
            setShowSnackBarAlert({
                status: true,
                type: 'success',
                message: res?.message
            });
        });
    };

    const handleSnackbarClose = () => {
        setShowSnackBarAlert({ status: false, type: null, message: null });
    };

    const formatError = (error?: unknown) => {
        if (typeof error !== 'string') return '';
        return error.charAt(0).toUpperCase() + error.slice(1).toLowerCase();
    };


    const newRows = rows.filter((r) => r.isNew);
    const savedRows = rows.filter((r) => !r.isNew);

    return (
        <Dialog open fullWidth maxWidth="md" onClose={onClose}>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
                Endorsement Attachments
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                {/* -------- HEADER + ADD -------- */}

                <Box textAlign="center" mt={0}>
                    <IconButton color="primary" onClick={addRow} disabled={newRowStatus}>
                        <AddCircleOutlineIcon fontSize="large" />
                    </IconButton>

                    <Typography display="block" mb={1}>
                        Supported formats: .jpg, .png, .pdf, .doc, .docx, .xls, .xlsx
                    </Typography>
                </Box>

                {/* -------- LOADER -------- */}
                {loadingList && (
                    <Box textAlign="center" mt={3}>
                        <img src={loader} alt="loader" width={90} />
                    </Box>
                )}

                {/* -------- NEW ROWS -------- */}
                {!loadingList &&
                    newRows.map((row) => (
                        <Formik
                            key={row.tempId}
                            initialValues={{
                                documentName: '',
                                file: null
                            }}
                            validationSchema={rowSchema}
                            onSubmit={(values) => saveRow(row, values)}
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleChange,
                                setFieldValue,
                                submitForm
                            }) => {
                                const hasFileError = touched.file && Boolean(errors.file);

                                return (
                                    <Grid
                                        container
                                        spacing={2}
                                        alignItems="flex-start"
                                        sx={{ mb: 1 }}
                                    >
                                        {/* -------- DOCUMENT NAME -------- */}
                                        <Grid item xs={5}>
                                            <TextField
                                                select
                                                size="small"
                                                fullWidth
                                                label={
                                                    <>
                                                        Document Name <span style={{ color: 'red' }}>*</span>
                                                    </>
                                                }
                                                name="documentName"
                                                value={values.documentName}
                                                onChange={handleChange}
                                                error={
                                                    touched.documentName &&
                                                    Boolean(errors.documentName)
                                                }
                                                helperText={
                                                    touched.documentName &&
                                                    errors.documentName
                                                }
                                                FormHelperTextProps={{
                                                    sx: { minHeight: 18 }
                                                }}
                                            >
                                                {documentNames.map((item) => (
                                                    <MenuItem
                                                        key={item.id}
                                                        value={item.name}
                                                    >
                                                        {item.name}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>

                                        {/* -------- FILE UPLOAD -------- */}
                                        <Grid item xs={4}>
                                            <Button
                                                component="label"
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                    height: 37,
                                                    borderColor: hasFileError
                                                        ? 'error.main'
                                                        : 'primary.main',
                                                    color: hasFileError
                                                        ? 'error.main'
                                                        : 'primary.main',
                                                    '&:hover': {
                                                        borderColor: hasFileError
                                                            ? 'error.dark'
                                                            : 'primary.dark'
                                                    }
                                                }}
                                            >
                                                {values.file?.name ? (
                                                    values.file.name
                                                ) : (
                                                    <>
                                                        Choose File  <span style={{ color: 'red', fontWeight: '500', marginLeft: '3px' }}>  *</span>
                                                    </>
                                                )}
                                                <input
                                                    hidden
                                                    type="file"
                                                    accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx"
                                                    onChange={(e) =>
                                                        setFieldValue(
                                                            'file',
                                                            e.target.files?.[0] || null
                                                        )
                                                    }
                                                />
                                            </Button>

                                            {/* -------- FILE ERROR (sentence case) -------- */}
                                            <Box minHeight={18} mt={0.5}>
                                                {hasFileError && (
                                                    <Typography
                                                        color="error"
                                                        fontWeight={600}
                                                        sx={{ textTransform: 'none' }}
                                                    >
                                                        {formatError(errors.file)}
                                                    </Typography>
                                                )}

                                            </Box>
                                        </Grid>

                                        {/* -------- ACTIONS -------- */}
                                        <Grid item xs={3} textAlign="right">
                                            {savingRowId === row.tempId ? (
                                                <CircularProgress size={22} />
                                            ) : (
                                                <>
                                                    <IconButton
                                                        color="primary"
                                                        onClick={submitForm}
                                                    >
                                                        <SaveIcon />
                                                    </IconButton>

                                                    <IconButton
                                                        color="error"
                                                        onClick={() => deleteRow(row)}
                                                    >
                                                        <CloseIcon />
                                                    </IconButton>
                                                </>
                                            )}
                                        </Grid>
                                    </Grid>
                                );
                            }}
                        </Formik>
                    ))}


                {/* -------- SAVED DOCUMENTS -------- */}
                {!loadingList && savedRows.length === 0 && newRows.length === 0 && (
                    <NoDataAvailable />
                )}

                {!loadingList && savedRows.length > 0 && (
                    <Box mt={3}>
                        <Typography style={{ fontWeight: '800' }}>
                            Uploaded Documents
                        </Typography>

                        {savedRows.map((row) => (
                            <Grid
                                key={row.id}
                                container
                                spacing={2}
                                alignItems="center"
                                sx={{ mb: 1 }}
                            >
                                <Grid item xs={8}>
                                    <Typography>
                                        {row.documentName}
                                    </Typography>
                                </Grid>

                                {/* <Grid item xs={4}>
                                    <Button
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        disabled
                                    >
                                        {row.documentUrl
                                            ?.split('/')
                                            .pop()}
                                    </Button>
                                </Grid> */}

                                <Grid item xs={4} textAlign="right">
                                    {savingRowId === row.id ? (
                                        <CircularProgress size={22} />
                                    ) : (
                                        <>
                                            <IconButton
                                                color="primary"
                                                onClick={() =>
                                                    window.open(
                                                        row.documentUrl,
                                                        '_blank'
                                                    )
                                                }
                                            >
                                                <DownloadIcon />
                                            </IconButton>
                                            <IconButton
                                                color="error"
                                                onClick={() =>
                                                    deleteRow(row)
                                                }
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </>
                                    )}
                                </Grid>
                            </Grid>
                        ))}
                    </Box>
                )}
            </DialogContent>

            <SnackbarAlert
                showSnackBarAlert={showSnackBarAlert}
                handleSnackbarClose={handleSnackbarClose}
            />
        </Dialog>
    );
};

export default EndorsementAttachmentDialog;
